import { useEffect, useState } from 'react';
import {
  can,
  formatJalali,
  formatPercent,
  formatRial,
  ROLE_LABELS_FA,
  toFaDigits as fa,
  type Role,
} from '@kia/shared';
import { api, errMsg, getAuthToken } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import { marginColor } from '../lib/margin';
import { isReleasePrepEligible } from '../lib/releasePrep';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorRetry,
  Field,
  Input,
  Modal,
  Textarea,
  useConfirm,
  useToast,
  type ChipTone,
} from '../ui';
import './releases.css';

/**
 * ترخیص و امضای دیجیتال (CIRCULARITY_360 D4). The human-facing side of the two-person signed release:
 * prepare a حواله from a Confirmed deal → CHECK (re-auth + warehouse gate) → APPROVE by a *different*
 * person → SECURITY releases at the gate (creates the delivery) → چاپ حواله. Every signing step
 * re-verifies the signer's password server-side; buttons self-gate by role AND state so the UI never
 * offers an action the server would reject.
 */

type RState = 'Draft' | 'Checked' | 'Approved' | 'Released' | 'Cancelled';
interface RRow {
  id: number;
  deal_id: number;
  state: RState;
  gate_verdict: string;
  override_reason: string;
  prepared_by: string;
  prepared_at: string;
  released_by: string | null;
  released_at: string | null;
  gate_note: string;
  deal_no: string;
}
interface RSig {
  slot: 'check' | 'approve';
  signer: string;
  signed_at: string;
}
interface RLine {
  product_code: string;
  qty: number;
}
interface REconLine {
  product_code: string;
  name: string;
  qty: number;
  unitFull: number;
  unitPrice: number;
  dayMargin: number | null;
  confirmed: number;
  lineValue: number;
}
interface REcon {
  customer_name: string;
  releaseValue: number;
  redFloor: number;
  amberFloor: number;
  priceAge: number | null;
  healthy: boolean;
  lines: REconLine[];
}
interface Loaded {
  release: RRow;
  lines: RLine[];
  signatures: RSig[];
  deal_no: string;
  /** the economics a signer is vouching for — same computeDeal the gate runs (TKT-1). */
  economics?: REcon | null;
}
interface SigVerify {
  slot: 'check' | 'approve';
  name: string;
  role: string;
  signed_at: string;
  valid: boolean;
}
interface Integrity {
  contentHash: string;
  contentMatches: boolean;
  fullySigned: boolean;
  signatures: SigVerify[];
}

// Deal picker + its remaining-per-line, for preparing a new release.
interface DealLite {
  id: number;
  no: string;
  state: string;
  customer?: { name: string } | null;
}
interface DealLine {
  product_code: string;
  name: string;
  remaining: number;
}

const STATE_FA: Record<RState, string> = {
  Draft: 'پیش‌نویس',
  Checked: 'بررسی‌شده',
  Approved: 'تأییدشده',
  Released: 'ترخیص‌شده',
  Cancelled: 'لغوشده',
};
const STATE_TONE: Record<RState, ChipTone> = {
  Draft: 'neutral',
  Checked: 'info',
  Approved: 'warn',
  Released: 'success',
  Cancelled: 'danger',
};
const SLOT_FA: Record<'check' | 'approve', string> = {
  check: 'بررسی و تأیید فنی',
  approve: 'تأیید ترخیص',
};
// Signed-release timestamps go through the app's ONE Jalali formatter (Tehran calendar) — not a raw ISO
// substring — so this control screen matches every other date in the app.
const dt = (iso: string | null) => (iso ? formatJalali(iso) : '—');

export function ReleasesScreen({
  focusRelease = null,
  onFocused,
  focusReleaseDeal = null,
  onPrepareDealFocused,
}: {
  /** deep-link from search/notification (E4-06): expand + load this release row on arrival. */
  focusRelease?: number | null;
  onFocused?: () => void;
  /** deep-link from a deal's «آماده‌سازی حواله» button (FLOW-1): pre-seed PrepareCard with this DEAL id. */
  focusReleaseDeal?: number | null;
  onPrepareDealFocused?: () => void;
} = {}) {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const role = user?.role;
  const relRes = useApiResource<RRow[]>('/api/releases');
  const rows = relRes.data ?? [];
  const [open, setOpen] = useState<number | null>(null); // expanded release id
  const [detail, setDetail] = useState<Record<number, Loaded>>({});
  const [integrity, setIntegrity] = useState<Record<number, Integrity>>({});
  // The signing ceremony modal.
  const [act, setAct] = useState<{
    id: number;
    kind: 'check' | 'approve' | 'release' | 'reject';
  } | null>(null);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const canPrepare = role ? can(role, 'release.prepare') : false;
  const canCheck = role ? can(role, 'release.check') : false;
  const canApprove = role ? can(role, 'release.approve') : false;
  const canSecurity = role ? can(role, 'release.security') : false;

  // The list loads through useApiResource, so a failed read lands in status:'error' with a retry — NOT the
  // misleading "no releases" empty state (a 500 must never read as "no signed releases" on this control).
  const refresh = () => relRes.reload(true);

  async function loadDetail(id: number) {
    const [d, v] = await Promise.all([
      api<Loaded>(`/api/releases/${id}`),
      api<Integrity>(`/api/releases/${id}/verify`),
    ]);
    if (d.ok) setDetail((m) => ({ ...m, [id]: d.body as Loaded }));
    else toast(errMsg(d, 'خطا در بارگذاری جزئیات حواله'), 'error');
    if (v.ok) setIntegrity((m) => ({ ...m, [id]: v.body as Integrity }));
  }

  const toggle = (id: number) => {
    const next = open === id ? null : id;
    setOpen(next);
    if (next !== null && !detail[next]) void loadDetail(next);
  };

  // Deep-link focus (E4-06): a search hit / notification for a release expands + loads its row on arrival,
  // instead of dumping the user at the top of the list to re-hunt it.
  useEffect(() => {
    if (focusRelease == null) return;
    setOpen(focusRelease);
    if (!detail[focusRelease]) void loadDetail(focusRelease);
    onFocused?.();
    // Trigger on the incoming id only; detail/loadDetail/onFocused are intentionally not deps.
  }, [focusRelease]);

  // When the signing ceremony opens, make sure this release's economics are loaded — the signer must see the
  // margin/cost they're vouching for even if they never expanded the row (TKT-1).
  useEffect(() => {
    if (act && !detail[act.id]) void loadDetail(act.id);
    // Trigger on the acted-on id only.
  }, [act?.id]);

  const printDoc = (id: number) =>
    window.open(`/api/releases/${id}/document?t=${getAuthToken() ?? ''}`, '_blank');

  const closeModal = () => {
    setAct(null);
    setPassword('');
    setReason('');
    setNote('');
  };

  async function submitAction() {
    if (!act) return;
    setBusy(true);
    try {
      let res;
      if (act.kind === 'check') {
        res = await api(`/api/releases/${act.id}/check`, {
          method: 'POST',
          body: JSON.stringify({ password, override_reason: reason }),
        });
      } else if (act.kind === 'approve') {
        res = await api(`/api/releases/${act.id}/approve`, {
          method: 'POST',
          body: JSON.stringify({ password }),
        });
      } else if (act.kind === 'reject') {
        res = await api(`/api/releases/${act.id}/reject`, {
          method: 'POST',
          body: JSON.stringify({ reason }),
        });
      } else {
        res = await api(`/api/releases/${act.id}/release`, {
          method: 'POST',
          body: JSON.stringify({ note }),
        });
      }
      if (res.ok) {
        toast(
          act.kind === 'check'
            ? 'بررسی و امضا ثبت شد'
            : act.kind === 'approve'
              ? 'تأیید نهایی ثبت شد'
              : act.kind === 'reject'
                ? 'حواله برای اصلاح به آماده‌ساز بازگردانده شد'
                : 'ترخیص از درب ثبت شد — حواله صادر شد',
        );
        const id = act.id;
        closeModal();
        refresh();
        void loadDetail(id);
      } else {
        toast(errMsg(res, 'خطا در ثبت امضا'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id: number) {
    // Destructive on a signed document — confirm first (the app's styled RTL dialog, not window.confirm).
    const ok = await confirm({
      title: 'لغو حواله',
      message: `حوالهٔ #${fa(id)} لغو شود؟ امضاهای ثبت‌شده باطل می‌شوند و این کار بازگشت‌پذیر نیست.`,
      confirmLabel: 'لغو حواله',
      cancelLabel: 'انصراف',
      danger: true,
    });
    if (!ok) return;
    // Explicit empty body: api() always sets a JSON content-type, and Fastify rejects a JSON content-type with
    // an empty body (FST_ERR_CTP_EMPTY_JSON_BODY) before the handler.
    const res = await api(`/api/releases/${id}/cancel`, { method: 'POST', body: '{}' });
    if (res.ok) {
      toast('حواله لغو شد');
      refresh();
      setDetail((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
    } else toast(errMsg(res, 'خطا در لغو حواله'), 'error');
  }

  async function verify(id: number) {
    const v = await api<Integrity>(`/api/releases/${id}/verify`);
    if (v.ok) {
      const iv = v.body as Integrity;
      setIntegrity((m) => ({ ...m, [id]: iv }));
      toast(
        iv.fullySigned
          ? 'اصالت تأیید شد — همهٔ امضاها معتبر'
          : iv.contentMatches
            ? 'محتوا دست‌نخورده؛ امضاها هنوز کامل نیست'
            : 'هشدار: محتوا با امضاها هم‌خوان نیست',
        iv.fullySigned ? 'default' : 'error',
      );
    }
  }

  const actRow = act ? rows.find((r) => r.id === act.id) : undefined;
  // The below-margin override reason is REQUIRED (≥10 chars) only when the gate flagged this release — surface
  // that in the modal and enforce it client-side, so the checker isn't rejected only AFTER re-entering a password.
  const isOverride = act?.kind === 'check' && actRow?.gate_verdict === 'override';
  const reasonOk = !isOverride || reason.trim().length >= 10;

  // Worklist ordering (TKT-2): the list was a firehose of every release ever, most of which the viewer can't
  // touch. Float the ones where it is THIS viewer's turn to sign to the top and mark them, so the screen reads
  // as "what needs me now" — not a ledger to scan.
  const myTurn = (r: RRow) =>
    (r.state === 'Draft' && canCheck) ||
    (r.state === 'Checked' && canApprove) ||
    (r.state === 'Approved' && canSecurity);
  const sortedRows = [...rows].sort((a, b) => Number(myTurn(b)) - Number(myTurn(a)) || b.id - a.id);
  const myTurnCount = rows.filter(myTurn).length;

  return (
    <>
      {canPrepare ? (
        <PrepareCard
          onCreated={refresh}
          focusDealId={focusReleaseDeal}
          onDealFocused={onPrepareDealFocused}
        />
      ) : null}

      <Card title="ترخیص و امضای دیجیتال — حواله‌های خروج">
        {relRes.status === 'error' ? (
          <ErrorRetry error={relRes.error} onRetry={relRes.reload} />
        ) : relRes.status === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon="🚚"
            title="هنوز حواله‌ای نیست"
            hint="حواله از یک قرارداد قطعی ساخته می‌شود؛ پس از دو امضای «بررسی» و «تأیید»، حراست کالا را در گیت ترخیص می‌کند."
          />
        ) : (
          <div className="rel-list">
            {myTurnCount > 0 ? (
              <div className="banner banner-warn" style={{ marginBottom: 4 }}>
                {fa(myTurnCount)} حواله منتظر اقدام شماست — در بالای فهرست نشان‌دار شده‌اند.
              </div>
            ) : null}
            {sortedRows.map((r) => {
              const d = detail[r.id];
              const iv = integrity[r.id];
              return (
                <div key={r.id} className="rel-row">
                  <div className="rel-head">
                    <div className="rel-title">
                      <b>حوالهٔ #{fa(r.id)}</b>
                      <span className="muted">{r.deal_no}</span>
                      <Chip tone={STATE_TONE[r.state]}>{STATE_FA[r.state]}</Chip>
                      {myTurn(r) ? <Chip tone="warn">● نوبت شما</Chip> : null}
                      {r.gate_verdict === 'override' ? (
                        <Chip tone="warn">تأیید مدیر (زیر حد)</Chip>
                      ) : null}
                      {iv ? (
                        <Chip
                          tone={iv.fullySigned ? 'success' : iv.contentMatches ? 'info' : 'danger'}
                        >
                          {iv.fullySigned
                            ? '✓ اصالت'
                            : iv.contentMatches
                              ? 'در جریان'
                              : '✗ ناهم‌خوان'}
                        </Chip>
                      ) : r.state !== 'Draft' && r.state !== 'Cancelled' ? (
                        // A signed row whose integrity hasn't been checked yet shows an explicit "unverified"
                        // chip — absence of a ✓ must never read as "verified safe" on a fraud control.
                        <Chip tone="neutral">اصالت بررسی‌نشده</Chip>
                      ) : null}
                    </div>
                    <div className="rel-actions">
                      {r.state === 'Draft' && canCheck ? (
                        <Button onClick={() => setAct({ id: r.id, kind: 'check' })}>
                          بررسی و امضا
                        </Button>
                      ) : null}
                      {r.state === 'Checked' && canApprove ? (
                        <Button onClick={() => setAct({ id: r.id, kind: 'approve' })}>
                          تأیید نهایی
                        </Button>
                      ) : null}
                      {r.state === 'Approved' && canSecurity ? (
                        <Button onClick={() => setAct({ id: r.id, kind: 'release' })}>
                          ترخیص از درب
                        </Button>
                      ) : null}
                      {(r.state === 'Checked' || r.state === 'Approved') && canCheck ? (
                        // Send-back: an objecting checker/approver returns the release to the preparer with a
                        // reason instead of being stuck between signing it and letting it rot.
                        <Button
                          variant="secondary"
                          onClick={() => setAct({ id: r.id, kind: 'reject' })}
                        >
                          بازگرداندن
                        </Button>
                      ) : null}
                      <Button variant="ghost" onClick={() => printDoc(r.id)}>
                        چاپ حواله
                      </Button>
                      <Button variant="ghost" onClick={() => verify(r.id)}>
                        بررسی اصالت
                      </Button>
                      {r.state !== 'Released' && r.state !== 'Cancelled' && canPrepare ? (
                        <Button variant="secondary" onClick={() => cancel(r.id)}>
                          لغو
                        </Button>
                      ) : null}
                      <Button variant="ghost" onClick={() => toggle(r.id)}>
                        {open === r.id ? 'بستن' : 'جزئیات'}
                      </Button>
                    </div>
                  </div>

                  {open === r.id && d ? (
                    <div className="rel-detail">
                      {d.economics ? (
                        <>
                          <div className="muted" style={{ marginBottom: 8 }}>
                            مشتری: <b>{d.economics.customer_name || '—'}</b> · ارزش این حواله:{' '}
                            <b className="num">{formatRial(d.economics.releaseValue)}</b> ریال
                            {d.economics.priceAge != null
                              ? ` · کهنگی قیمت: ${fa(d.economics.priceAge)} روز`
                              : ''}
                            {!d.economics.healthy ? (
                              <span style={{ color: 'var(--red)' }}> · نرخ جذب ناسالم</span>
                            ) : null}
                          </div>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>کالا</th>
                                <th>تعداد</th>
                                <th>بهای تمام‌شده</th>
                                <th>قیمت واحد</th>
                                <th>حاشیه</th>
                                <th>ارزش خط</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.economics.lines.map((l) => {
                                // Same traffic-light as everywhere else — the ONE helper, fed this deal's
                                // live server-supplied floors. Neutral (no color) when the margin is null.
                                const tone = marginColor(l.dayMargin, d.economics!) ?? 'inherit';
                                return (
                                  <tr key={l.product_code}>
                                    <td>{l.name}</td>
                                    <td className="num">{fa(l.qty)}</td>
                                    <td className="num">{formatRial(l.unitFull)}</td>
                                    <td className="num">{formatRial(l.unitPrice)}</td>
                                    <td className="num" style={{ color: tone, fontWeight: 600 }}>
                                      {l.dayMargin != null ? formatPercent(l.dayMargin, 1) : '—'}
                                    </td>
                                    <td className="num">{formatRial(l.lineValue)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <table className="table">
                          <thead>
                            <tr>
                              <th>کد کالا</th>
                              <th>تعداد</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.lines.map((l) => (
                              <tr key={l.product_code}>
                                <td>{l.product_code}</td>
                                <td>{fa(l.qty)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <div>
                        {(['check', 'approve'] as const).map((slot) => {
                          const sv = iv?.signatures.find((s) => s.slot === slot);
                          return (
                            <div key={slot} className="sig-line">
                              <span className="muted">{SLOT_FA[slot]}:</span>{' '}
                              {sv ? (
                                <>
                                  {sv.name} ({ROLE_LABELS_FA[sv.role as Role] ?? sv.role}) ·{' '}
                                  {dt(sv.signed_at)}{' '}
                                  <Chip tone={sv.valid ? 'success' : 'danger'}>
                                    {sv.valid ? '✓ معتبر' : '✗ نامعتبر'}
                                  </Chip>
                                </>
                              ) : (
                                <span className="muted">امضا نشده</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {r.released_by ? (
                        <div className="muted">
                          تحویل درب / حراست: {r.released_by} · {dt(r.released_at)}
                          {r.gate_note ? ` · ${r.gate_note}` : ''}
                        </div>
                      ) : null}
                      {r.override_reason ? (
                        <div className="muted">دلیل تأیید مدیر: {r.override_reason}</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {act ? (
        <Modal
          title={
            act.kind === 'check'
              ? 'بررسی و امضای حواله'
              : act.kind === 'approve'
                ? 'تأیید نهایی (توسط شخص دوم)'
                : act.kind === 'reject'
                  ? 'بازگرداندن حواله به آماده‌ساز'
                  : 'ترخیص از درب (حراست)'
          }
          onClose={closeModal}
        >
          <div className="rel-form">
            {/* What you're signing: the economics (same computeDeal the gate runs) so no one signs blind. */}
            {detail[act.id]?.economics ? (
              <div
                className="banner"
                style={{ margin: '0 0 4px', fontSize: '.9em', lineHeight: 1.9 }}
              >
                <div>
                  مشتری: <b>{detail[act.id]!.economics!.customer_name || '—'}</b> · ارزش:{' '}
                  <b className="num">{formatRial(detail[act.id]!.economics!.releaseValue)}</b> ریال
                </div>
                {detail[act.id]!.economics!.lines.map((l) => {
                  const ec = detail[act.id]!.economics!;
                  // Same traffic-light as everywhere else — the ONE helper, fed this deal's live
                  // server-supplied floors. Muted (--ink-2) when the margin is null.
                  const tone = marginColor(l.dayMargin, ec) ?? 'var(--ink-2)';
                  return (
                    <div key={l.product_code}>
                      {l.name} — {fa(l.qty)} عدد · حاشیه{' '}
                      <b style={{ color: tone }}>
                        {l.dayMargin != null ? formatPercent(l.dayMargin, 1) : '—'}
                      </b>{' '}
                      (کف {formatPercent(ec.amberFloor, 0)})
                    </div>
                  );
                })}
              </div>
            ) : null}
            {act.kind === 'check' || act.kind === 'approve' ? (
              <>
                <p className="muted">
                  برای امضای دیجیتال، گذرواژهٔ خود را دوباره وارد کنید.
                  {act.kind === 'approve' ? ' امضاکنندهٔ دوم باید غیر از بررسی‌کننده باشد.' : ''}
                </p>
                <Field label="گذرواژه">
                  <Input
                    type="password"
                    value={password}
                    autoFocus
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
              </>
            ) : act.kind === 'reject' ? (
              <p className="muted">
                این حواله به آماده‌ساز بازگردانده می‌شود و امضاهای ثبت‌شده باطل می‌شوند. دلیل را
                بنویسید تا آماده‌ساز بداند چه چیزی را باید اصلاح کند.
              </p>
            ) : (
              <p className="muted">
                با ثبت ترخیص، حوالهٔ خروج نهایی و دستور تحویل کالا صادر می‌شود.
              </p>
            )}
            {act.kind === 'reject' ? (
              <Field label="دلیل بازگرداندن (الزامی)">
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
              </Field>
            ) : null}
            {act.kind === 'check' ? (
              <>
                {isOverride ? (
                  <p style={{ margin: 0, color: 'var(--amber)', lineHeight: 1.9 }}>
                    این حواله زیر حد حاشیهٔ مجاز است — برای امضا باید دلیلِ تأیید مدیر (حداقل ۱۰
                    نویسه) وارد شود.
                  </p>
                ) : null}
                <Field label="دلیل تأیید مدیر (فقط اگر زیر حد حاشیه باشد — حداقل ۱۰ نویسه)">
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
                </Field>
              </>
            ) : null}
            {act.kind === 'release' ? (
              <Field label="یادداشت درب (اختیاری)">
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            ) : null}
            <div className="row-end">
              <Button
                onClick={submitAction}
                disabled={
                  busy ||
                  ((act.kind === 'check' || act.kind === 'approve') && !password) ||
                  (act.kind === 'reject' && !reason.trim()) ||
                  !reasonOk
                }
              >
                {act.kind === 'release'
                  ? 'ثبت ترخیص'
                  : act.kind === 'reject'
                    ? 'بازگرداندن'
                    : 'امضا و ثبت'}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/** Prepare a Draft release: pick a Confirmed/Delivered deal → set qty per still-open line. */
function PrepareCard({
  onCreated,
  focusDealId = null,
  onDealFocused,
}: {
  onCreated: () => void;
  /** deep-link from a deal's «آماده‌سازی حواله» button (FLOW-1): pre-seed this card with the deal on arrival. */
  focusDealId?: number | null;
  onDealFocused?: (() => void) | undefined;
}) {
  const toast = useToast();
  const dealsRes = useApiResource<DealLite[]>('/api/deals');
  const deals = (dealsRes.data ?? []).filter((d) => isReleasePrepEligible(d.state));
  const [dealId, setDealId] = useState<number | null>(null);
  const [lines, setLines] = useState<DealLine[]>([]);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  // The preparer's worklist size — Confirmed deals are the ones still awaiting a dispatch (a fully-delivered
  // deal auto-advances to Delivered), so this is the count of orders ready to turn into a حواله.
  const readyCount = deals.filter((d) => d.state === 'Confirmed').length;

  async function pickDeal(id: number) {
    setDealId(id);
    setLines([]);
    setQty({});
    const r = await api<{ compute: { lines: DealLine[] } }>(`/api/deals/${id}`);
    if (r.ok) {
      const open = (r.body as { compute: { lines: DealLine[] } }).compute.lines.filter(
        (l) => l.remaining > 0,
      );
      setLines(open);
      setQty(Object.fromEntries(open.map((l) => [l.product_code, l.remaining])));
    }
  }

  // Deep-link from a deal's «آماده‌سازی حواله» button (FLOW-1): run the SAME pickDeal the dropdown runs, so the
  // card arrives pre-seeded with the deal + its open-line quantities. pickDeal fetches /api/deals/${id} itself
  // and works before the deal list loads, so there's no race on the dropdown. Trigger on the incoming id only.
  useEffect(() => {
    if (focusDealId == null) return;
    void pickDeal(focusDealId);
    onDealFocused?.();
    // Trigger on the incoming id only; pickDeal/onDealFocused are intentionally not deps.
  }, [focusDealId]);

  async function submit() {
    if (dealId === null) return;
    // Clamp each line to its still-open quantity — the HTML `max` isn't enforced on manual typing, so a
    // hand-typed over-quantity would otherwise be sent and rejected only by the server.
    const overAsked = lines.some((l) => Number(qty[l.product_code] ?? 0) > l.remaining);
    const payload = lines
      .map((l) => ({
        product_code: l.product_code,
        qty: Math.min(Math.max(0, Number(qty[l.product_code] ?? 0)), l.remaining),
      }))
      .filter((l) => l.qty > 0);
    if (payload.length === 0) {
      toast('حداقل یک قلم با تعداد لازم است', 'error');
      return;
    }
    if (overAsked) toast('تعداد به باقی‌ماندهٔ قابل ترخیص محدود شد'); // benign auto-clamp, not an error
    setBusy(true);
    try {
      const r = await api('/api/releases', {
        method: 'POST',
        body: JSON.stringify({ deal_id: dealId, lines: payload }),
      });
      if (r.ok) {
        toast('حوالهٔ پیش‌نویس ساخته شد');
        setDealId(null);
        setLines([]);
        setQty({});
        onCreated();
      } else toast(errMsg(r, 'خطا در ساخت حواله'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`آماده‌سازی حواله — ${readyCount > 0 ? `${fa(readyCount)} قرارداد آمادهٔ ترخیص` : 'قراردادی آمادهٔ ترخیص نیست'}`}
    >
      <div className="rel-form">
        <Field label="قرارداد قطعی">
          <select
            className="input"
            value={dealId ?? ''}
            onChange={(e) =>
              e.target.value ? void pickDeal(Number(e.target.value)) : setDealId(null)
            }
          >
            <option value="">— انتخاب قرارداد —</option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.no}
                {d.customer?.name ? ` — ${d.customer.name}` : ''}
              </option>
            ))}
          </select>
        </Field>
        {lines.length > 0 ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>کالا</th>
                  <th>باقی‌مانده</th>
                  <th>تعداد ترخیص</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.product_code}>
                    <td>
                      {l.name} <span className="muted">({l.product_code})</span>
                    </td>
                    <td>{fa(l.remaining)}</td>
                    <td>
                      <Input
                        type="number"
                        min={0}
                        max={l.remaining}
                        value={String(qty[l.product_code] ?? 0)}
                        onChange={(e) =>
                          setQty((q) => ({ ...q, [l.product_code]: Number(e.target.value) }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row-end">
              <Button onClick={submit} disabled={busy}>
                آماده‌سازی حواله
              </Button>
            </div>
          </>
        ) : dealId !== null ? (
          <p className="muted">این قرارداد قلم بازی برای ترخیص ندارد.</p>
        ) : null}
      </div>
    </Card>
  );
}
